import { FC } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { ConfirmationOfApplication } from '@/features/ui/Steps/ConfirmationOfApplication';

import { IntegrationSystem } from '@/shared/types';
import { Informer, StepControlPanel } from '@/shared/ui';

import { FormPreviewer } from '@/components/Forms/FormPreviewer/FormPreviewer';

import { config } from '@/config/index';

import { generatePayloadForCreationOfInvoice } from '@/modules/pay/pages/services/ServicePaymentWithoutApplication/lib/generatePayload';
import { getOperIdFromUrl } from '@/modules/pay/pages/services/ServicePaymentWithoutApplication/lib/utils';
import { ServicePaymentDataProps } from '@/modules/pay/pages/services/ServicePaymentWithoutApplication/model/types';

import { formatCacheDate } from '@/helpers/dates';

import { PaymentServiceResponseOption } from '@/api/types';

import api from '@/api';

import { localStorageController } from '../../../../../../../Cache';
import { PAY_01_OPER_ID, PAY_01_REDIRECT_URI } from '../../lib/constants';
import { ServicePaymentMergedSchema } from '../../model/schemas';

function formatServiceName(
  service: Nullable<PaymentServiceResponseOption>,
  subService?: Nullable<PaymentServiceResponseOption>,
): Nullable<string> {
  return subService ? `${service?.label} ${subService.label}` : service?.label;
}

function formatServiceCode(
  service: Nullable<PaymentServiceResponseOption>,
  subService?: Nullable<PaymentServiceResponseOption>,
): Nullable<string> {
  return subService
    ? subService?.data?.find((item) => item.id === subService?.id)?.code
    : service?.data?.find((item) => item.id === service.id)?.code;
}

export interface ServicePaymentPreviewOwnProps {
  checkDefraymentServiceAvailability?: (formData: ServicePaymentDataProps) => Promise<boolean>;
  isPymentServiceAvailable?: boolean;
  setIsPaymentServiceAvailable: (isAvailable: boolean) => void;
}

export const ServicePaymentPreview: FC<StepProps<ServicePaymentPreviewOwnProps>> = ({
  next,
  prev,
  checkDefraymentServiceAvailability,
  isPymentServiceAvailable = false,
  setIsPaymentServiceAvailable,
}) => {
  const { getValues, watch } = useFormContext<ServicePaymentMergedSchema>();

  const { fullName, rnokpp, orgNpu, defraymentService, defraymentSubService, paymentInfo } = getValues();
  const confirmEnteredDataCorrect = watch('confirmEnteredDataCorrect');

  const defraymentServiceName = formatServiceName(defraymentService, defraymentSubService);
  const defraymentServiceCode = formatServiceCode(defraymentService, defraymentSubService);

  const handleNextStep = async () => {
    const formData = getValues();

    try {
      const res = await api[IntegrationSystem.PAY].services.createInvoice(
        generatePayloadForCreationOfInvoice({ formData }),
      );

      const redirectOperId = getOperIdFromUrl(res.redirect_uri);

      localStorageController.setCachedData({
        [PAY_01_OPER_ID]: {
          data: redirectOperId ?? '',
          meta: {
            app_version: config.VERSION,
            date_of_cache: formatCacheDate(new Date()) ?? '',
          },
        },
      });
      localStorageController.setCachedData({
        [PAY_01_REDIRECT_URI]: {
          data: res.redirect_uri ?? '',
          meta: {
            app_version: config.VERSION,
            date_of_cache: formatCacheDate(new Date()) ?? '',
          },
        },
      });
    } finally {
      const isServiceAvailable = await checkDefraymentServiceAvailability?.(formData);
      if (isServiceAvailable) {
        next();
      }
    }
  };

  const handlePrev = () => {
    setIsPaymentServiceAvailable(true);
    prev();
  };

  return (
    <>
      <FormBlock title="Підтвердження даних">
        <FormPreviewer
          data={[
            ['Послуга', defraymentServiceName ?? ''],
            ['Код послуги', defraymentServiceCode ?? ''],
            ['Вартість, грн', paymentInfo?.price ?? ''],
            ['Комісія платіжної системи, грн', paymentInfo?.fee.toString() ?? ''],
            ['Орган НПУ', orgNpu?.label ?? ''],
            ['ПІБ', fullName],
            ['Ідентифікаційний код (РНОКПП)', rnokpp],
          ]}
        />
      </FormBlock>

      <ConfirmationOfApplication shouldBeAbleToUpdateProfile={false} />

      {!isPymentServiceAvailable && (
        <div>
          <Informer
            bgColor="sand"
            icon={{ name: 'info', color: 'red' }}
            header={{ text: 'Помилка', color: 'red' }}
            text={{
              color: 'black',
              text: 'Для вибраної послуги тимчасово відсутня можливість оплатити послугу онлайн. Спробуйте оплатити пізніше.',
            }}
          />
        </div>
      )}

      <StepControlPanel
        next={handleNextStep}
        prev={handlePrev}
        disabledNext={!confirmEnteredDataCorrect || !isPymentServiceAvailable}
      />
    </>
  );
};
